import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api/endpoints';
import { ConfirmDialog } from '../components/Common';
import type { EmergencyRevokeResponse } from '../types';

export function AdminSecurityPage() {
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastResult, setLastResult] = useState<EmergencyRevokeResponse | null>(null);

  const emergencyRevoke = useMutation({
    mutationFn: () => adminAPI.emergencyRevoke(),
    onSuccess: (response) => {
      setShowConfirm(false);
      setLastResult(response.data);
    },
    onError: () => {
      setShowConfirm(false);
      toast.error('Emergency revoke failed. Check server logs and try again.');
    },
  });

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-xl) mx-auto w-full p-6 flex-1 flex flex-col min-h-0 space-y-8">
        <h1 className="text-2xl font-bold text-dark-text-primary tracking-tight">Security</h1>

        <div className="card animate-scaleIn border-red-900/40">
          <div className="px-4 py-3 border-b border-red-900/30 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <h2 className="text-lg font-semibold text-red-400">Emergency Actions</h2>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-6">
              <div>
                <h3 className="font-semibold text-dark-text-primary mb-1">Revoke All Sessions</h3>
                <p className="text-sm text-dark-text-muted max-w-xl">
                  Immediately signs out every user from all devices. Revokes all refresh tokens and
                  clears all Google OAuth tokens system-wide. Use this if a breach is detected.
                  All users will need to log in again.
                </p>
              </div>
              <button
                onClick={() => setShowConfirm(true)}
                className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/20 border border-red-700 text-red-400 hover:bg-red-900/40 hover:text-red-300 transition-colors text-sm font-medium"
              >
                <AlertTriangle className="w-4 h-4" />
                Revoke Everything
              </button>
            </div>

            {lastResult && (
              <div className="p-4 rounded-lg bg-dark-elevated border border-dark-border text-sm space-y-1">
                <p className="font-medium text-dark-text-primary">Last emergency revoke</p>
                <p className="text-dark-text-secondary">
                  {new Date(lastResult.revoked_at).toLocaleString()} —{' '}
                  <span className="text-dark-text-primary">{lastResult.refresh_tokens_revoked}</span> refresh tokens revoked,{' '}
                  <span className="text-dark-text-primary">{lastResult.google_tokens_cleared}</span> Google tokens cleared
                  {lastResult.google_revoke_errors > 0 && (
                    <span className="text-orange-400"> ({lastResult.google_revoke_errors} Google revoke errors)</span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        <ConfirmDialog
          isOpen={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => emergencyRevoke.mutate()}
          title="Revoke All Sessions — Are you sure?"
          message="This will immediately sign out EVERY user from ALL devices, revoke all refresh tokens, and clear all Google OAuth tokens. This cannot be undone. All users will need to log in again."
          confirmLabel="Revoke Everything"
          isLoading={emergencyRevoke.isPending}
          danger
        />
      </div>
    </div>
  );
}
